import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { calcularLimites } from '@/map/bounds';
import { useCamadas } from '@/map/camadas';
import { obterFeicao, obterGeometria } from '@/services/geo-engine/requests';
import { useChamada } from '@/services/geo-engine/useChamada';
const ContextoDaSelecao = createContext(null);
export function SelecaoProvider({ children }) {
    const { enquadrar } = useCamadas();
    const detalhe = useChamada();
    const geometria = useChamada();
    const [selecionada, setSelecionada] = useState(null);
    // Refletindo o par `(camadaId, id)` atual, este marcador é checado a cada `.then`
    // para descartar respostas de uma seleção anterior. Evita que o resultado de uma
    // requisição antiga sobrescreva a seleção corrente quando o usuário troca de
    // feição em sequência rápida.
    const atual = useRef(null);
    const { executar: executarDetalhe, limparErro: limparErroDoDetalhe } = detalhe;
    const { executar: executarGeometria, limparErro: limparErroDaGeometria } = geometria;
    const selecionar = useCallback(async (camadaId, id, opcoes) => {
        // Limpa imediatamente qualquer feição selecionada, sem aguardar a
        // resolução das requisições. Garante que o inspetor não exibe dados
        // obsoletos enquanto a nova seleção é carregada.
        atual.current = { camadaId, id };
        setSelecionada({ camadaId, id, feicao: null, geometria: null });
        const aindaAtual = () => atual.current?.camadaId === camadaId && atual.current.id === id;
        const deveEnquadrar = opcoes?.enquadrar === true;
        // Detalhe e geometria individual são independentes: uma falha não esconde a outra.
        await Promise.all([
            executarDetalhe((sinal) => obterFeicao(camadaId, id, sinal)).then((feicao) => {
                if (feicao !== null && aindaAtual()) {
                    setSelecionada((anterior) => (anterior === null ? anterior : { ...anterior, feicao }));
                }
            }),
            executarGeometria((sinal) => obterGeometria(camadaId, id, sinal)).then((resultado) => {
                if (resultado === null || !aindaAtual()) {
                    return;
                }
                setSelecionada((anterior) => anterior === null ? anterior : { ...anterior, geometria: resultado });
                if (deveEnquadrar) {
                    const limites = calcularLimites(resultado.geometria);
                    if (limites !== null) {
                        enquadrar(limites);
                    }
                }
            }),
        ]);
    }, [executarDetalhe, executarGeometria, enquadrar]);
    const limpar = useCallback(() => {
        atual.current = null;
        setSelecionada(null);
        limparErroDoDetalhe();
        limparErroDaGeometria();
    }, [limparErroDoDetalhe, limparErroDaGeometria]);
    const valor = useMemo(() => ({
        selecionada,
        carregando: detalhe.carregando || geometria.carregando,
        erro: detalhe.erro ?? geometria.erro,
        selecionar,
        limpar,
    }), [
        selecionada,
        detalhe.carregando,
        geometria.carregando,
        detalhe.erro,
        geometria.erro,
        selecionar,
        limpar,
    ]);
    return _jsx(ContextoDaSelecao.Provider, { value: valor, children: children });
}
export function useSelecao() {
    const selecao = useContext(ContextoDaSelecao);
    if (selecao === null) {
        throw new Error('useSelecao exige SelecaoProvider acima na árvore');
    }
    return selecao;
}
